import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import ConsentPage from "pages/NHIS/ConsentPage";
import AuthPage from "pages/NHIS/AuthPage";
import MedicationListPage from "pages/NHIS/MedicationListPage";
import { NhisStatus } from "types/nhis";

// --- 미리보기용 목 데이터 ---

const MOCK_CONSENT_INFO = {
  pharmacistName: "김약사",
  patientName: "환자",
  purpose: "다제약물관리",
  status: "PENDING" as NhisStatus,
};

// 3단계 샘플 처방전 (추후 지니님 제공 데이터로 교체)
const MOCK_PRESCRIPTION_GROUPS = [
  {
    groupId: "preview-1",
    hospital: "OO내과의원",
    prescriptionDate: "2026-03-15",
    prescriptions: [
      { id: "p1", medicationName: "아스피린정 100mg" },
      { id: "p2", medicationName: "아토르바스타틴정 20mg" },
      { id: "p3", medicationName: "암로디핀정 5mg" },
    ],
  },
  {
    groupId: "preview-2",
    hospital: "XX정형외과",
    prescriptionDate: "2026-03-10",
    prescriptions: [
      { id: "p4", medicationName: "셀레콕시브캡슐 200mg" },
      { id: "p5", medicationName: "레바미피드정 100mg" },
    ],
  },
];

// --- 단계별 안내 텍스트 ---

const STEP_GUIDE: Record<number, string[]> = {
  0: [
    "환자에게 '약학AI PhAI' 채널을 통해 카카오 알림톡이 발송됩니다.",
    "환자가 알림톡의 '내 약 내역 전송하기' 버튼을 누르면 1단계 화면으로 이동합니다.",
  ],
  1: [
    "휴대폰 본인인증이 가장 쉽습니다. 고령 환자는 이 방법을 권장합니다.",
    "인증수단을 선택하고 주민번호를 입력한 뒤 '다음 단계로'를 누르면 2단계로 넘어갑니다.",
  ],
  2: [
    "휴대폰 본인인증: 문자(SMS)로 받은 인증번호 6자리를 입력하고 '인증번호 확인'을 누르면 3단계로 넘어갑니다.",
    "(1단계에서) 다른 인증수단을 선택한 경우: 해당 앱으로 이동해 인증을 완료한 뒤 이 페이지로 돌아와야 합니다.",
    "(1단계에서) 카카오톡을 선택한 경우: 이 화면을 잠시 나가 '카카오톡 지갑' 채널에서 인증을 완료한 후, 처음에 받은 '약학AI PhAI' 알림톡을 다시 눌러 이 페이지로 돌아와주세요.",
    "다른 앱을 다녀오는 동안에도 이 2단계 화면은 그대로 유지됩니다.",
  ],
  3: [
    "심평원에서 조회된 처방내역 중 전달할 처방전을 선택하고 '약사님께 전달하기'를 누르면 완료됩니다.",
    "⚠️ 3단계에서 이탈하면 조회된 처방전이 사라집니다. 환자가 \"처음부터 다시 시도\"를 누르면 같은 링크에서 재개할 수 있습니다.",
  ],
};

// --- 스타일 ---

const PreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StepRow = styled.div`
  display: flex;
  gap: 24px;
  overflow-x: auto;
`;

const StepCard = styled.div`
  flex-shrink: 0;
  width: 220px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StepLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
`;

const StepDot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  background: ${COLOR_SYSTEM.get("Blue")?.[500]};
  color: #ffffff;
`;

const MOBILE_WIDTH = 375;
const CARD_WIDTH = 220;

const INNER_PADDING = 10;
const INNER_WIDTH = CARD_WIDTH - INNER_PADDING * 2;
const INNER_SCALE = INNER_WIDTH / MOBILE_WIDTH;

const PreviewContainer = styled.div<{ $height?: number }>`
  position: relative;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 12px;
  overflow: hidden;
  height: ${({ $height }) => $height ?? 380}px;
  background: #ffffff;
`;

const PreviewInner = styled.div<{ $extraTopPadding?: number }>`
  padding: ${({ $extraTopPadding }) => INNER_PADDING + ($extraTopPadding ?? 0)}px
    ${INNER_PADDING}px ${INNER_PADDING}px;
`;

const PreviewScale = styled.div`
  transform: scale(${INNER_SCALE});
  transform-origin: top left;
  width: ${MOBILE_WIDTH}px;
  pointer-events: none;
  user-select: none;
`;


const GuideList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
`;

const GuideItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const GuideBullet = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${COLOR_SYSTEM.get("Gray")?.[400]};
  flex-shrink: 0;
  margin-top: 7px;
`;

// --- 카카오 알림톡 목업 ---

const KakaoMock = styled.div`
  width: 100%;
  height: 100%;
  background: #b1c7db;
  padding: 14px 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const KakaoChannelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const KakaoProfile = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
`;

const KakaoChannelName = styled.span`
  font-weight: 700;
  font-size: 13px;
  color: #14203a;
`;

const KakaoMessageBox = styled.div`
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
`;

const KakaoAlimtalkHeader = styled.div`
  background: #fee110;
  padding: 8px 12px;
  font-weight: 700;
  font-size: 13px;
  color: #14203a;
`;

const KakaoMessageBody = styled.div`
  padding: 10px 12px 12px;
  font-size: 11px;
  line-height: 1.45;
  color: #14203a;
  white-space: pre-line;
`;

const KakaoMessageButton = styled.div`
  margin-top: 10px;
  padding: 8px;
  background: #f5f9fa;
  border: 1px solid #b8c1cc;
  border-radius: 8px;
  text-align: center;
  font-size: 12px;
  color: #14203a;
`;

const KakaoAlimtalkMock: React.FC = () => (
  <KakaoMock>
    <KakaoChannelRow>
      <KakaoProfile>💊</KakaoProfile>
      <KakaoChannelName>약학AI PhAI</KakaoChannelName>
    </KakaoChannelRow>
    <KakaoMessageBox>
      <KakaoAlimtalkHeader>알림톡 도착</KakaoAlimtalkHeader>
      <KakaoMessageBody>
        📢 신청하신 약 상담 준비를 위해 요청드립니다
        {"\n\n"}
        ㅇㅇㅇ님 안녕하세요. 드시고 계신 약을 모두 확인해서 혹시 겹치는
        약이나 주의할 점은 없는지 살펴드리려 합니다. 건강보험심사평가원에
        기록된 약 처방 내역을 확인할 수 있도록 동의를 부탁드립니다.
        <KakaoMessageButton>내 약 내역 전송하기</KakaoMessageButton>
      </KakaoMessageBody>
    </KakaoMessageBox>
  </KakaoMock>
);


// --- 단계 정보 ---

const STEP_NAMES = [
  "카카오 알림톡 수신",
  "1단계: 동의 및 인증수단 선택",
  "2단계: 본인인증",
  "3단계: 처방전 전달",
];

// --- 컴포넌트 ---

const NhisStepPreview: React.FC = () => {
  const noop = () => {};

  return (
    <PreviewWrapper>
      <Text textType="Body2Bold" color={COLOR_SYSTEM.get("Gray")?.[600]}>
        환자가 거치는 화면
      </Text>
      <StepRow>
        {[0, 1, 2, 3].map((step) => (
          <StepCard key={step}>
            <StepLabel>
              <StepDot>{step}</StepDot>
              <Text
                textType="Caption"
                color={COLOR_SYSTEM.get("Gray")?.[600]}
                style={{ fontWeight: 600 }}
              >
                {STEP_NAMES[step]}
              </Text>
            </StepLabel>
            <PreviewContainer $height={step === 1 ? 650 : 380}>
              {step === 0 ? (
                <KakaoAlimtalkMock />
              ) : (
                <PreviewInner>
                  <PreviewScale>
                    {step === 1 && (
                      <ConsentPage
                        consentInfo={MOCK_CONSENT_INFO}
                        token=""
                        onStatusChange={noop}
                        previewMode
                      />
                    )}
                    {step === 2 && (
                      <AuthPage
                        token=""
                        onStatusChange={noop}
                        authMethodType="PHONE_VERIFY"
                      />
                    )}
                    {step === 3 && (
                      <MedicationListPage
                        token=""
                        prescriptionGroups={MOCK_PRESCRIPTION_GROUPS as any}
                        onStatusChange={noop}
                      />
                    )}
                  </PreviewScale>
                </PreviewInner>
              )}
            </PreviewContainer>
            <GuideList>
              {STEP_GUIDE[step].map((line, i) => (
                <GuideItem key={i}>
                  <GuideBullet />
                  <Text
                    textType="Caption"
                    color={COLOR_SYSTEM.get("Gray")?.[500]}
                    style={{ lineHeight: "1.5" }}
                  >
                    {line}
                  </Text>
                </GuideItem>
              ))}
            </GuideList>
          </StepCard>
        ))}
      </StepRow>
    </PreviewWrapper>
  );
};

export default NhisStepPreview;
